import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useAppDispatch, type RootState} from '../../../store/store';
import {getSelectedSpeaker, selectSelectedPhoneNumber, setMicRequired, setPhoneNumber, setSelectedPhoneNumber} from '../../../store/features/calls/dialerSlice';
import {selectUserId} from '../../../store/features/Auth/authSlice';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {clearCallMetadata} from '../../../store/features/MatrixEvents/MatrixEventsService';
import {useFetchMsisdnsListQuery} from '../../../store/features/MsisdnSettings/msisdnSettingsService';
import {initializeSip} from '../../../components/Dialer/services/sipService';
import {initializeMatrixCallHandlers} from '../../../services/matrixCallService';
import {getAccountBlocked} from '../../../services/accountAccessState';
import {CallStateEnum} from '../../../components/Dialer/callState';

const MIC_TOAST = {
    description: 'Microphone is turned off. Turn it on to use this feature.',
    type: 'error',
    dontHide: true,
    position: 'center',
    icon: 'mic_off',
} as const;

const CALL_PATH = /^\/call\/([^/]+)/;

let sipStarted = false;

export const useTelephonyRuntime = (): void => {
    const dispatch = useAppDispatch();
    const role = useSelector((state: RootState) => state.auth.role);
    const speaker = useSelector(getSelectedSpeaker);
    const userId = useSelector(selectUserId);
    const selectedLine = useSelector(selectSelectedPhoneNumber);
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const incomingCall = useSelector((state: RootState) => state.dialer.incomingCall);
    const micRequired = useSelector((state: RootState) => state.dialer.micRequired);
    const {data: lines} = useFetchMsisdnsListQuery({userId});

    const initialSpeaker = useRef(speaker);

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            void Notification.requestPermission();
        }
        initializeMatrixCallHandlers(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (sipStarted || role === 'SUPERADMIN' || getAccountBlocked()) {
            return;
        }
        sipStarted = true;
        initializeSip(dispatch, initialSpeaker.current);
    }, [dispatch, role]);

    useEffect(() => {
        const match = CALL_PATH.exec(window.location.pathname);
        if (match) {
            dispatch(setPhoneNumber(decodeURIComponent(match[1])));
        }
    }, [dispatch]);

    useEffect(() => {
        let cancelled = false;
        void navigator.permissions?.query({name: 'microphone' as PermissionName}).then(status => {
            if (cancelled) {
                return;
            }
            const apply = () => {
                const denied = status.state === 'denied';
                dispatch(setMicRequired(denied));
            };
            apply();
            status.onchange = apply;
        }).catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedLine && lines?.data?.length) {
            dispatch(setSelectedPhoneNumber(lines.data[0]));
        }
    }, [dispatch, lines, selectedLine]);

    useEffect(() => {
        if (callState === CallStateEnum.IDLE) {
            dispatch(clearCallMetadata());
        }
    }, [callState, dispatch]);

    useEffect(() => {
        if (incomingCall && micRequired) {
            dispatch(showToast(MIC_TOAST));
        }
    }, [dispatch, incomingCall, micRequired]);
};
